<template>

    <div v-if="loadingStates.fetching" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <div class="bg-gray-100 px-4 py-3 rounded-lg flex items-center justify-between cursor-pointer w-full h-12"></div>
        <div class="bg-gray-100 px-4 py-3 rounded-lg flex items-center justify-between cursor-pointer w-full h-12"></div>
        <div class="bg-gray-100 px-4 py-3 rounded-lg flex items-center justify-between cursor-pointer w-full h-12"></div>
    </div>

    <div v-if="!loadingStates.fetching" class="grid grid-cols-1 gap-4">
        <div v-for="(environment, index) in environments" :key="`environment-item-${environment.id}`" class="w-full relative">
            <Dropdown class="w-full">
                <template #button>
                    <MenuButton :class="[
                        'bg-white px-4 py-3 rounded-lg flex items-center cursor-pointer w-full focus:border-primary-500',
                        environment.default ? 'border-primary-500 border-2' : 'border border-gray-200 hover:border-gray-300'
                    ]">
                        <div class="flex items-center space-x-2 mr-2">
                            <EyeOffIcon v-if="environment.hidden" class="w-6 h-6 text-gray-500" />
                            <StarIcon v-if="environment.default" class="w-6 h-6 text-primary-500" />
                        </div>
                        <h3 class="font-semibold">{{ environment.school_year }}</h3>
                    </MenuButton>
                </template>
                <template #menu>
                    <MenuItem v-if="!environment.default && !environment.hidden">
                        <button
                            type="button"
                            :class="dropdownItem"
                            @click.prevent="setDefaultEnvironment(environment)"
                        >
                            <StarIcon class="w-6 h-6 mr-2" />
                            Set as default
                        </button>
                    </MenuItem>
                    <MenuItem v-if="!environment.hidden">
                        <button
                            type="button"
                            :class="dropdownItem"
                            @click="hideEnvironment(environment, true)"
                        >
                            <EyeOffIcon class="w-6 h-6 mr-2" />
                            Hide
                        </button>
                    </MenuItem>
                    <MenuItem v-if="environment.hidden">
                        <button
                            type="button"
                            :class="dropdownItem"
                            @click="hideEnvironment(environment, false)"
                        >
                            <EyeIcon class="w-6 h-6 mr-2" />
                            Visible
                        </button>
                    </MenuItem>
                </template>
            </Dropdown>
            <div class="absolute top-0 bottom-0 right-0 flex items-center flex-col h-full justify-center mr-4">
                <a href="#" :class="[
                    index !== 0 ? 'opacity-100 hover:opacity-60 cursor-pointer' : 'opacity-25 cursor-default',
                ]" :disabled="index === 0" @click.prevent="changeOrder(environment, 'up')">
                    <ChevronUpIcon class="w-5 h-5" />
                </a>
                <a href="#" :class="[index === environments.length - 1 ? 'opacity-25 cursor-default' : 'opacity-100 hover:opacity-60 cursor-pointer']" :disabled="index === environments.length - 1" @click.prevent="changeOrder(environment, 'down')">
                    <ChevronDownIcon class="w-5 h-5" />
                </a>
            </div>
        </div>
        <div>
            <button type="button" class="bg-gray-100 px-4 py-3 rounded-lg flex items-center cursor-pointer w-full focus:border-primary-500" @click="openModal()">
                <PlusCircleIcon class="w-6 h-6 mr-2" /> New environment
            </button>
            <TransitionRoot appear :show="environment.isOpen" as="template">
                <Dialog as="div" class="relative z-10">
                    <TransitionChild
                        as="template"
                        enter="duration-300 ease-out"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="duration-200 ease-in"
                        leave-from="opacity-100"
                        leave-to="opacity-0"
                    >
                        <div class="fixed inset-0 bg-black bg-opacity-25" />
                    </TransitionChild>

                    <div class="fixed inset-0 overflow-y-auto">
                        <FormKit 
                            type="form"
                            :actions="false"
                            @submit="createEnvironment"
                            :errors="['']"
                            form-class="flex min-h-full items-center justify-center p-4 text-center"
                            :classes="{
                                message: 'hidden'
                            }"
                        >
                            <TransitionChild
                                as="template"
                                enter="duration-300 ease-out"
                                enter-from="opacity-0 scale-95"
                                enter-to="opacity-100 scale-100"
                                leave="duration-200 ease-in"
                                leave-from="opacity-100 scale-100"
                                leave-to="opacity-0 scale-95"
                            >
                                <DialogPanel
                                    class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
                                >
                                    <DialogTitle
                                        as="h3"
                                        class="text-lg font-medium leading-6 text-gray-900"
                                    >
                                         New Environment
                                    </DialogTitle>

                                    <div class="mt-6">
                                        <div class="grid grid-cols-1 gap-4">
                                            <FormKit
                                                type="text"
                                                v-model="environment.value"
                                                label="Environment name"
                                                validation="required|string"
                                                validation-visibility="submit"
                                                :classes="formElement"
                                            />
                                        </div>
                                    </div>

                                    <div class="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <button 
                                            type="button" 
                                            class="btn btn-white"
                                            @click="closeModal()"
                                        >
                                            <XIcon class="w-5 h-5 mr-2" />
                                            Close
                                        </button>
                                        <button 
                                            type="submit" 
                                            class="btn btn-primary"
                                        >
                                            Save
                                        </button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </FormKit>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>
    </div>
</template>
<script setup>
import { onMounted, reactive, ref } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import axios from 'axios'
import Dropdown from '@/Shared/Dropdown'
import {
  
} from '@headlessui/vue'
import { 
    MenuButton,
    MenuItem, 
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogPanel,
    DialogTitle 
} from '@headlessui/vue'
import { useDialogStyles, useDropdownStyles } from '@/composables/styles.js'
import { useAlert } from '@/composables/alert.js'
import { useFormStyles } from '@/composables/styles.js'
import { 
    XIcon,
    EyeOffIcon, 
    StarIcon, 
    EyeIcon, 
    PlusCircleIcon, 
} from '@heroicons/vue/outline'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/solid'

onMounted(() => fetchEnvironments())

const { formElement } = useFormStyles()

const environments = ref([])

const environment = reactive({
    isOpen: false,
    value: null,
})

const loadingStates = reactive({
    fetching: false,
    creating: false,
    defaultEnvironment: false,
    hideEnvironment: false,
    ordering: false
})

const { dropdownItem } = useDropdownStyles()

const { showAlert, confirm } = useAlert()

const event = new Event('environments:refresh')

function openModal() {
    environment.isOpen = true
}

function closeModal() {
    environment.isOpen = false
}

async function fetchEnvironments() {
    loadingStates.fetching = true;

    try {
        const { data } = await axios.get(route('api.configuration.environments'))

        environments.value = data.data.map((environment, order) => {
            environment.order = environment.order ? environment.order : order + 1

            return environment
        })
    } catch (e) {
        // @TODO handle error
    } finally {
         loadingStates.fetching = false;
    }
}

async function setDefaultEnvironment(environment) {
    const didConfirm = await confirm('Change default environment', {
        text: `Are you sure do you want to set ${environment.school_year} as default environment?`
    })

    if (! didConfirm) {
        return
    }

    loadingStates.defaultEnvironment = true;

    Inertia.put(route('api.configuration.environments.default', environment.id), {}, {
        onSuccess: () => {
            showAlert(`Default environment is now ${environment.school_year}`, {
                icon: 'success',
                showConfirmButton: false,
            })

            const event = new Event('environments:active:changed')
            
            window.dispatchEvent(event)

            fetchEnvironments()
        },
        onFinish: () => loadingStates.defaultEnvironment = false
    })
}

async function hideEnvironment(environment, shouldHide) {

    const didConfirm = await confirm(shouldHide ? 'Hide environment' : 'Unhide environment', {
        text: shouldHide ? `Are you sure do you want to hide ${environment.school_year} environment?` : `Are you sure do you want to unhide ${environment.school_year} environment?`
    })

    if (! didConfirm) {
        return
    }

    loadingStates.hideEnvironment = true;

    Inertia.put(route('api.configuration.environments.hide', environment.id), {
        hide: shouldHide
    }, {
        onSuccess: () => {
            showAlert(shouldHide ? `${environment.school_year} is hidden` : `${environment.school_year} is now visible`, {
                icon: 'success',
                showConfirmButton: false
            })

            fetchEnvironments()

            window.dispatchEvent(event)
        },
        onFinish: () => loadingStates.hideEnvironment = false
    })
}

async function createEnvironment() {
    loadingStates.creating = true;

    Inertia.post(route('api.configuration.environments.create'), {
        environment: environment.value
    }, {
        onSuccess: () => {
            showAlert(`New environment added!`, {
                icon: 'success',
                showConfirmButton: false,
                text: 'You might need to reload the page to refresh the environments menu'
            })
            window.dispatchEvent(event)

            fetchEnvironments()
        },
        onFinish: () => { 
            environment.isOpen = false
            environment.value = null
            loadingStates.creating = false 
        }
    })
}

async function changeEnvironmentOrder(data) {
    loadingStates.ordering = true;

    Inertia.post(route('api.configuration.environments.order'), data, {
        onSuccess: () => { 
            window.dispatchEvent(event)

            fetchEnvironments() 
        },
        onFinish: () => { 
            loadingStates.ordering = false 
        }
    })
}

function changeOrder(environment, order) {
    switch (order) {
        case 'up':
            let previousOrder = environments.value.filter((env) => env.order === environment.order - 1)[0]
            previousOrder.order = environment.order

            environment.order = environment.order - 1

            changeEnvironmentOrder({
                affected: previousOrder,
                environment: environment
            });
            break;
        case 'down':
            let nextOrder = environments.value.filter((env) => env.order === environment.order + 1)[0]
            nextOrder.order = environment.order

            environment.order = environment.order + 1

            changeEnvironmentOrder({
                affected: nextOrder,
                environment: environment
            });

            break;
    }
}
</script>